/**
 * Le dialogue avec l'Espace Pilotage (« la coque »), selon le contrat des logiciels de la coque,
 * version 4 (espace-pilotage-front, `docs/contrat-des-logiciels.md` ; cadrage 2026-031 de CDT,
 * espace-pilotage-cadrage 2026-002). Dans son cadre, à l'adresse `?embarque=1`, CDT publie ses
 * rubriques au menu de la coque, qui lui renvoie les clics, son thème et l'avis que les droits
 * ont changé.
 *
 *   CDT → coque : {type:'dode:ready', app, contrat}  {type:'dode:menu', app, vue, ver, items, ctx}
 *                 {type:'dode:auth', app}   (pas de session, ou plus de session)
 *   coque → CDT : {type:'dode:go', vue | 'toggle:<id>'}  {type:'dode:theme', theme}
 *                 {type:'dode:profil'}      ({type:'dode:note'} est ignoré)
 *
 * Les messages ne partent que vers l'origine `coque` de `/config.json`, et ne sont acceptés que
 * d'elle, venant de la fenêtre parente (§ 4.1). Ce fichier ne touche ni à `window` ni à Angular :
 * tout ce qu'il décide s'éprouve sans navigateur.
 */

/** L'identifiant de CDT dans la coque, qui est aussi le préfixe de ses rôles (§ 1, § 6). */
export const SHELL_APP = 'cdt';

/** La version du contrat des logiciels que CDT suit. */
export const SHELL_CONTRACT_VERSION = 4;

/** Ce que l'application garde de la coque une fois embarquée. */
export interface Embedding {
  /** L'origine de la coque, sans barre finale. */
  coque: string;
  /** La fenêtre de la coque : `window.parent`, ou un factice dans les tests. */
  parent: Pick<Window, 'postMessage'>;
  /** La fenêtre de l'application, où arrivent les messages : `window`, ou un factice. */
  self: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

/** L'adresse demande le mode embarqué : `embarque=1`, exactement (§ 2). */
export function isEmbeddingRequested(search: string): boolean {
  return /[?&]embarque=1(&|$)/.test(search);
}

/**
 * Embarqué seulement à une adresse `?embarque=1`, réellement dans un cadre (`parent` non nul),
 * avec une origine `coque` déclarée. Sinon l'application s'affiche seule, comme sans le
 * paramètre : un lien `?embarque=1` copié ou mis en favori ne l'en empêche pas (§ 2).
 */
export function isEmbedded(coque: string, search: string, parent: unknown): boolean {
  return coque !== '' && parent !== null && parent !== undefined && isEmbeddingRequested(search);
}

/** Ce que la coque demande. */
export type ShellOrder =
  { go: string } | { toggle: string } | { theme: 'dark' | 'light' } | { profile: true };

export interface ReceivedMessage {
  origin: string;
  source: unknown;
  data: unknown;
}

/** Les caractères d'un identifiant de rubrique (§ 4.3). */
const RUBRIC_ID = /^[\w:=-]+$/;

/**
 * L'ordre que porte un message de la coque, ou `null` s'il ne vient pas d'elle — de son origine
 * et de la fenêtre parente — ou ne dit rien que CDT traite. `dode:note` en fait partie : CDT ne
 * garde rien des notes 💬 (cadrage 2026-031).
 */
export function readShellOrder(
  coque: string,
  parent: unknown,
  event: ReceivedMessage,
): ShellOrder | null {
  if (!coque || event.origin !== coque || !parent || event.source !== parent) {
    return null;
  }
  const data = event.data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const message = data as Record<string, unknown>;
  switch (message['type']) {
    case 'dode:go': {
      const vue = message['vue'];
      if (typeof vue !== 'string' || !RUBRIC_ID.test(vue)) {
        return null;
      }
      const toggle = /^toggle:(.+)$/.exec(vue);
      return toggle ? { toggle: toggle[1] } : { go: vue };
    }
    case 'dode:theme': {
      const theme = message['theme'];
      return theme === 'dark' || theme === 'light' ? { theme } : null;
    }
    case 'dode:profil':
      return { profile: true };
    default:
      return null;
  }
}

export function readyMessage() {
  return { type: 'dode:ready', app: SHELL_APP, contrat: SHELL_CONTRACT_VERSION } as const;
}

export function authMessage() {
  return { type: 'dode:auth', app: SHELL_APP } as const;
}

/** Une rubrique publiée au menu de la coque : CDT n'a ni regroupement ni compteur. */
export interface Rubric {
  id: string;
  lb: string;
}

/** Le dossier ouvert : le contrat affiché, auquel la coque rattache ses notes 💬. */
export interface ShellFolder {
  key: string;
  lb: string;
}

export interface ShellMenu {
  items: Rubric[];
  /** La rubrique affichée. */
  vue: string;
  /** La version de CDT, affichée sous son nom. */
  ver: string;
  ctx: ShellFolder | null;
}

export interface MenuMessage {
  type: 'dode:menu';
  app: typeof SHELL_APP;
  vue: string;
  ver?: string;
  items: (Rubric & { on: boolean })[];
  ctx: ShellFolder | null;
}

/** La version telle que la coque l'accepte ; une autre serait ignorée (§ 4.3). */
const VERSION = /^[\w .·/:-]{1,24}$/;

/**
 * Le menu de CDT, filtré comme la coque le filtrerait : ce qu'elle refuserait ne part pas, et ce
 * qu'elle couperait part coupé — le contrat ouvert garde ainsi la même clé des deux côtés.
 */
export function menuMessage({ items, vue, ver, ctx }: ShellMenu): MenuMessage {
  return {
    type: 'dode:menu',
    app: SHELL_APP,
    vue,
    ...(VERSION.test(ver) ? { ver } : {}),
    items: items.map(({ id, lb }) => ({ id, lb, on: id === vue })),
    ctx: ctx ? folder(ctx) : null,
  };
}

function folder({ key, lb }: ShellFolder): ShellFolder {
  return { key: key.replace(/[^\w:-]/g, '').slice(0, 60), lb: lb.slice(0, 90) };
}
